<template>
  <div
    class="fab fab-extended fab-center-bottom div-UCCodeScanner"
  >
    <a
      v-if="fabButtonShow"
      href="#"
      class="a-fab"
      :style="{
        'background': fabButtonBackgroundColor,
        'min-width': fabButtonMinWidth,
      }"
      @click="Open"
    >
      <img
        class="img-iconoFab"
        src="@/static/icons/code.svg"
      >
      <div class="fab-text fab-text-escaner">
        {{ fabButtonText || $t('common.scan').toUpperCase() }}
      </div>
    </a>
    <f7-popup
      class="escanerCodigoBarras-popup"
      :opened="popupOpened"
      @popup:closed="ClosePopup"
    >
      <div>
        <div
          class="escanerCodigoBarras-popup__cabecera"
        >
          <div>
            <span v-if="cabeceraPopup !== ''">{{ cabeceraPopup }}</span>
            <span v-else>
              {{ $t('codeScanner.common.qrReading') }}
            </span>
          </div>
          <f7-button
            button
            @click="ClosePopup"
          >
            <img :src="icClose">
          </f7-button>
        </div>
        <div
          v-if="subTitle"
          class="div-subtitle-scanner"
        >
          <span>{{ subTitle }}:</span>
          <span class="div-subtitle-scanner__texto">{{ subTitleValue }}</span>
        </div>
        <BasicScan
          ref="basicScanRef"
          :active-scan="activateCamera"
          :show-scan-overlay="showScanOverlay"
          @scanner:scan="EventScan"
        />

        <div
          v-if="allowManualCodeEntry"
          class="escanerCodigoBarras-popup__inputs"
          :class="{
            'disabled-element': disabledScan,
          }"
        >
          <PCCodeScannerTiposLecturas
            ref="PCCodeScannerTiposLecturas"
            modo-lectura-defecto="Codigo"
            :disabled="disabledScan"
            :inputmode="inputmode"
            @codigo-barras="EventBarCodeReading"
            @numero-serie="EventNumeroSerieReading"
            @focus-off="showScanOverlay = true"
            @focus-on="showScanOverlay = false"
          />
          <slot
            name="slotShowInfo"
          />
        </div>
        <InfoSheet
          :state="infoSheetParams.state"
          :opened="infoSheetParams.opened"
          :title="infoSheetParams.title"
          :subtitle1="infoSheetParams.subtitle1"
          :subtitle="infoSheetParams.subtitle"
          :state-text="infoSheetParams.stateText"
          :image-src="infoSheetParams.imageSrc"
          :show-incidence-image="infoSheetParams.showIncidenceImage"
          :issue-reason="infoSheetParams.issueReason"
          @closed="infoSheetParams.opened = false"
        />
        <f7-block
          v-if="!autoOpenCamera && !activateCamera"
          class="block-btn-camera"
        >
          <f7-button
            class="btn-camara"
            large
            raised
            fill
            color="green"
            @click="BtnOpenCamera"
          >
            {{ $t('common.camera') }}
          </f7-button>
        </f7-block>
      </div>
    </f7-popup>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import InfoSheet from '@/components/common/InfoSheet.vue';
import icClose from '@/static/icons/ic_close.svg';
import PCCodeScannerTiposLecturas from '@/components/production/commercialization/picking/PCCodeScannerTiposLecturas.vue';
import { Log } from '@/js/common/HIDebuggingSupport';
import { isDeviceCordova } from '@/js/utils/platform';
import BasicScan from './BasicScan.vue';

/** @import { PropType } from 'vue' */
/** @import { InfoSheetState } from '@erpagroapp/types' */

export default defineComponent({
  name: 'UCCodeScanner',

  components: {
    InfoSheet,
    BasicScan,
    PCCodeScannerTiposLecturas,
  },

  props: {
    openInformationBar: { type: Boolean, default: false },
    imageSrc: { type: String, default: null },
    title: { type: String, default: '' },
    subtitle1: { type: [String, Number], default: null },
    subtitle: { type: [String, Number], default: '' },
    stateText: { type: String, default: '' },
    issueReason: { type: String, default: null },
    showIncidenceImage: { type: Boolean, default: true },
    state: {
      type: /** @type {PropType<InfoSheetState>} */ (String),
      default: 'ENTER',
    },
    autofocusOnInput: { type: Boolean, default: false },
    fabButtonShow: { type: Boolean, default: true },
    fabButtonBackgroundColor: { type: String, default: '' },
    fabButtonMinWidth: { type: String, default: '220px' },
    fabButtonText: { type: String, default: '' },
    cabeceraPopup: { type: String, default: '' }, // Propiedad para pasarle al escaner un titulo para el popup
    subTitle: { type: String, default: '' }, // Propiedad para indicar el identificador del subtitulo
    subTitleValue: { type: String, default: '' }, // Propiedad para indicar el valor del subtitulo
    continuousBarCodeReading: { type: Boolean, default: false }, // La lectura se abre continuamente mientras no se pulse el boton de back.
    allowManualCodeEntry: { type: Boolean, default: false },
    autoOpenCamera: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    inputmode: {
      type: /** @type {import('vue').PropType<HTMLAttributes['inputmode']>} */ (
        String
      ),
      default: undefined,
    },
  },

  emits: [
    'code-reading',
    'popup-close',
    'codigo-barras',
    'numero-serie',
  ],

  data() {
    return {
      showScanOverlay: true,
      popupOpened: false,
      icClose,
      activateCamera: false,
      infoSheetParams: {
        opened: false,
        imageSrc: null,
        title: '',
        subtitle1: null,
        subtitle: '',
        stateText: '',
        issueReason: null,
        showIncidenceImage: true,
        /** @type {InfoSheetState} */
        state: 'DEFAULT',
      },
      disabledScan: false,
    };
  },
  watch: {
    async disabled(value) {
      this.disabledScan = value;
      if (this.autofocusOnInput) {
        this.SetFocusOnInputElement();
      }
    },
    openInformationBar(value) {
      this.infoSheetParams.opened = value;
    },
    imageSrc(value) {
      this.infoSheetParams.imageSrc = value;
    },
    title(value) {
      this.infoSheetParams.title = value;
    },
    subtitle1(value) {
      this.infoSheetParams.subtitle1 = value;
    },
    subtitleBar(value) {
      this.infoSheetParams.subtitle = value;
    },
    stateText(value) {
      this.infoSheetParams.stateText = value;
    },
    issueReason(value) {
      this.infoSheetParams.issueReason = value;
    },
    showIncidenceImage(value) {
      this.infoSheetParams.showIncidenceImage = value;
    },
    state(value) {
      this.infoSheetParams.state = value;
    },
  },
  created() {
    this.StartComponent();
  },

  methods: {
    StartComponent() {
      this.infoSheetParams.opened = this.openInformationBar;
      this.infoSheetParams.imageSrc = this.imageSrc;
      this.infoSheetParams.title = this.title;
      this.infoSheetParams.subtitle1 = this.subtitle1;
      this.infoSheetParams.subtitle = this.subtitle;
      this.infoSheetParams.stateText = this.stateText;
      this.infoSheetParams.issueReason = this.issueReason;
      this.infoSheetParams.showIncidenceImage = this.showIncidenceImage;
      this.infoSheetParams.state = this.state;
      this.disabledScan = this.disabled;
    },

    /**
     * @public
     * @returns {void}
     */
    Open() {
      this.popupOpened = true;
      if (this.autofocusOnInput) {
        this.SetFocusOnInputElement();
      }
      if (!this.autoOpenCamera) {
        return;
      }
      if (isDeviceCordova()) {
        this.activateCamera = true;
      }
    },

    BtnOpenCamera() {
      if (isDeviceCordova()) {
        this.activateCamera = true;
      }
    },

    /**
     * @public
     * @returns {void}
     */
    OpenAuto() {
      this.popupOpened = true;
      if (isDeviceCordova()) {
        this.activateCamera = true;
      }
    },
    EventScan(value) {
      Log(this, this.EventScan.name);
      if (this.disabledScan) {
        return;
      }
      this.$emit('code-reading', value);
      if (!this.continuousBarCodeReading) {
        this.ClosePopup();
      }
    },

    /**
     * @public
     * @returns {void}
     */
    ClosePopup() {
      if (!this.popupOpened) {
        return;
      }

      this.popupOpened = false;
      this.activateCamera = false;
      this.$emit('popup-close');
      if (isDeviceCordova()) {
        this.$refs.basicScanRef.stopScanner();
      }
    },

    /**
     * @public
     * @returns {void}
     */
    SetFocusOnInputElement() {
      Log(this, this.SetFocusOnInputElement.name);
      this.$refs.PCCodeScannerTiposLecturas.SetFocusOnInputElement();
    },

    EventBarCodeReading(code) {
      Log(this, this.EventBarCodeReading.name);
      this.$emit('codigo-barras', code);
      if (!this.continuousBarCodeReading) {
        this.ClosePopup();
      } else if (this.autofocusOnInput) {
        this.SetFocusOnInputElement();
      }
    },
    EventNumeroSerieReading(code) {
      Log(this, this.EventNumeroSerieReading.name);
      this.$emit('numero-serie', code);
      if (!this.continuousBarCodeReading) {
        this.ClosePopup();
      } else if (this.autofocusOnInput) {
        this.SetFocusOnInputElement();
      }
    },

  },
});
</script>

<style scoped lang="scss">
.md .fab[class*="-bottom"] {
  position: fixed;
}

.md .fab > a,
.md .fab-buttons a {
  --f7-fab-extended-size: 58px;

  font-family: var(--f7-font-family);
  box-sizing: border-box;
  color: #fff;
  background: #66ba33;
  padding: 5px;
  min-width: 220px;
}

.disabled-element {
  opacity: 0.5;
  pointer-events: none;
}

.img-iconoFab {
  float: left;
  position: absolute;
  left: 6px;
}

.fab-text {
  padding-left: 12%;
}

.block-btn-camera {
  display: flex;
  flex-direction: row;
  align-content: space-around;
  align-items: center;
  text-align: right;
  width: 100%;
  z-index: 100;
  position: fixed;
  bottom: 8px;
  right: 0;
  padding-top: 2vh;

  .btn-camara {
    width: 100%;
    margin-left: 5vh;
    margin-right: 5vh;
    color: #fff;
  }
}

.page {
  background: var(--his-tierra-white);
}

.div-animacionBarCode {
  display: flex;
  text-align: center;
  align-items: center;
}

/** Animation bar-code **/
.center {
  margin: 0 auto;
  text-align: center;
}

.spacer {
  margin-top: 6rem;
}

.anim-box {
  position: relative;
  width: 220px;
  height: 100px;
  padding: 25px 30px;
}

.anim-box::before,
.anim-box::after,
.anim-box > :first-child::before,
.anim-box > :first-child::after {
  position: absolute;
  width: 10%;
  height: 15%;
  border-color: #fff;
  border-style: solid;
  content: " ";
}

.anim-box::before {
  top: 0;
  left: 0;
  border-width: 2px 0 0 2px;
}

.anim-box::after {
  top: 0;
  right: 0;
  border-width: 2px 2px 0 0;
}

.anim-box > :first-child::before {
  bottom: 0;
  right: 0;
  border-width: 0 2px 2px 0;
}

.anim-box > :first-child::after {
  bottom: 0;
  left: 0;
  border-width: 0 0 2px 2px;
}

.anim-item {
  display: inline-block;
  background-color: #282829;
  height: 100px;
}

.anim-item-sm {
  width: 2px;
  margin-right: 3px;
}

.anim-item-md {
  width: 3px;
  margin-right: 2px;
}

.anim-item-lg {
  width: 5px;
  margin-right: 5px;
}

.scanner {
  width: 100%;
  height: 3px;
  background-color: red;
  opacity: 0.7;
  position: relative;
  box-shadow: 0 0 8px 10px rgb(170 11 23 / 49%);
  top: 50%;
  animation-name: scan;
  animation-duration: 4s;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}

@keyframes scan {
  0% {
    box-shadow: 0 0 8px 10px rgb(170 11 23 / 49%);
    top: 50%;
  }

  25% {
    box-shadow: 0 6px 8px 10px rgb(170 11 23 / 49%);
    top: 5px;
  }

  75% {
    box-shadow: 0 -6px 8px 10px rgb(170 11 23 / 49%);
    top: 98%;
  }
}

.div-input {
  background: #fff;
  margin-right: 18px;
  margin-left: 18px;
  border: 1px solid var(--his-silver);
  border-radius: 8px;
  min-height: 60px;
  margin-top: 16px;
  margin-bottom: 16px;
  box-sizing: border-box;
}

.input-searchCodeBars {
  width: 100%;
  height: 100%;
  padding-left: 19px;
  padding-right: 19px;
  color: #24253da8;
  font-size: 17px;
  margin-top: 22px;
  margin-bottom: 16px;
}

.input-busquedaCodigos {
  width: 100%;
  height: 100%;
  padding-left: 19px;
  padding-right: 19px;
  color: #24253da8;
  font-size: 17px;
  margin-top: 22px;
}

.div-modoInput {
  margin-top: 16px;
  margin-bottom: 0;
}

.div-btnModoInput {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  margin-left: 15px;
  border: 1px solid var(--his-silver);
  border-radius: 8px;
  height: 100%;
  width: 100%;
  min-height: 60px;
  box-shadow: 0 6px 10px #6666662b;
  box-sizing: border-box;
}

.img-btnModoInput {
  min-height: 36px;
  width: 45%;
}

.scan-code {
  z-index: 1;
  overflow-y: hidden;
  position: absolute;
  top: 45%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 65%;
  height: 37%;
  border-radius: 15px;
  box-shadow: 0 0 0 2000px rgb(0 0 0 / 40%);

  @media (max-width: 320px) {
    top: 50%;
  }

  @media (max-width: 360px) {
    top: 53%;
  }
}

.escanerCodigoBarras-popup {
  font-size: 17px;
  font-weight: 600;
  height: 100%;
  width: 100%;
  left: 0;
  top: 0;
  margin-left: 0;
  margin-top: 0;

  &__cabecera {
    color: var(--code-scanner-text-color);
    background-color: var(--code-scanner-bg-color);
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-left: 20px;
    padding-right: 20px;
    height: 55px;
  }

  &__inputs {
    background: var(--his-oliva);
  }
}

.div-subtitle-scanner {
  font-size: 17px;
  background: var(--his-slate-blue);
  padding-left: 20px;
  padding-top: 10px;
  padding-bottom: 10px;
  border-bottom: 0;
  border-top: 0;
  border-right: 0;
  border-left: 0;
  border-color: var(--his-disabled);
  border-style: solid;

  &__texto {
    font-weight: 300;
    margin-left: 15px;
    color: var(--his-blueberry-mist);
  }
}

/* .cabecera{
  background: var(--his-slate-blue);
  background: var(--his-slate-blue);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-left: 20px;
  padding-right: 20px;
  height: 55px;
} */
</style>
